import { useState } from 'react'
import {
  Box,
  VStack,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Input,
  FormControl,
  FormLabel,
  Button,
  Text,
  useToast,
} from '@chakra-ui/react'
import { useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { processNetCDF } from '../../utils/netcdf'
import { useDataStore, type Dataset } from '../../stores/dataStore'
import { read, utils } from 'xlsx-js-style'
import Papa from 'papaparse'
import { DatasetPreview } from './DatasetPreview'

export const DataConfig = () => {
  const toast = useToast()
  const { addDataset, datasets } = useDataStore()
  const [selectedDataset, setSelectedDataset] = useState<Dataset | null>(null)

  const onDrop = useCallback((acceptedFiles: File[]) => {
    console.log('Files dropped:', acceptedFiles)
    
    acceptedFiles.forEach(file => {
      console.log('Processing file:', file.name)
      const reader = new FileReader()
      
      reader.onload = () => {
        console.log('File read complete')
        if (reader.result instanceof ArrayBuffer) {
          try {
            if (file.name.endsWith('.nc')) {
              console.log('Processing NetCDF file')
              const ncData = processNetCDF(reader.result)
              addDataset({
                id: crypto.randomUUID(),
                name: file.name,
                type: 'nc',
                data: reader.result,
                metadata: {
                  dimensions: ncData.dimensions,
                  variables: ncData.variables,
                  attributes: ncData.attributes
                }
              })
            } else if (file.name.endsWith('.xlsx')) {
              console.log('Processing Excel file')
              const workbook = read(reader.result)
              addDataset({
                id: crypto.randomUUID(),
                name: file.name,
                type: 'xlsx',
                data: reader.result,
                metadata: {
                  sheets: workbook.SheetNames,
                  attributes: {
                    sheets: workbook.SheetNames,
                    parsedData: workbook.SheetNames.map(name => ({
                      sheet: name,
                      data: utils.sheet_to_json(workbook.Sheets[name])
                    }))
                  }
                }
              })
            } else if (file.name.endsWith('.csv')) {
              const text = new TextDecoder().decode(reader.result)
              
              const results = Papa.parse(text, { header: true })
              const columns = results.meta.fields || []
              
              // Transform the data immediately
              const parsedData = results.data
                .slice(0, 50)
                .map((row: unknown) => {
                  if (row && typeof row === 'object') {
                    return columns.reduce((acc, col) => ({
                      ...acc,
                      [col]: (row as Record<string, unknown>)[col]
                    }), {} as Record<string, unknown>)
                  }
                  return {} as Record<string, unknown>
                })

              const dataset: Dataset = {
                id: crypto.randomUUID(),
                name: file.name,
                type: 'csv',
                data: text,
                metadata: {
                  variables: columns.map(name => ({
                    name,
                    dimensions: [],
                    attributes: {},
                    type: 'string'
                  })),
                  attributes: {
                    rowCount: results.data.length,
                    columnCount: columns.length,
                    parsedData,
                    fields: columns
                  }
                }
              }

              console.log('Adding dataset to store:', {
                parsedData: parsedData[0],
                fields: columns
              })
              
              addDataset(dataset)
            }
            toast({
              title: "Dataset uploaded",
              description: `Successfully loaded ${file.name}`,
              status: "success",
              duration: 3000,
              isClosable: true,
            })
          } catch (error) {
            console.error('Error processing file:', error)
            toast({
              title: "Error loading dataset",
              description: error instanceof Error ? error.message : "Unknown error",
              status: "error",
              duration: 5000,
              isClosable: true,
            })
          }
        }
      }
      reader.readAsArrayBuffer(file)
    })
  }, [addDataset, toast])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/x-netcdf': ['.nc'],
      'text/csv': ['.csv'],
      'application/json': ['.json'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    }
  })

  const formatVariables = (variables: Array<{ name: string }> | undefined): string[] => {
    if (!variables) return []
    return variables
      .map(v => v.name)
      .filter(name => !name.toLowerCase().includes('index'))
  }

  return (
    <Box 
      p={4} 
      maxW="container.xl" 
      margin="0 auto"
      width="100%"
    >
      <Accordion allowMultiple>
        <AccordionItem>
          <h2>
            <AccordionButton width="100%">
              <Box flex="1" textAlign="left" fontWeight="bold">
                Datasets ({datasets.length})
              </Box>
              <AccordionIcon />
            </AccordionButton>
          </h2>
          <AccordionPanel>
            <VStack 
              align="stretch" 
              spacing={2} 
              width="100%"
            >
              {datasets.map((dataset) => (
                <Box
                  key={dataset.id}
                  p={2}
                  bg="gray.50"
                  borderRadius="md"
                  cursor="pointer"
                  onClick={() => setSelectedDataset(dataset)}
                  _hover={{ bg: 'gray.100' }}
                  width="100%"
                  maxWidth="100%"
                >
                  <Box 
                    display="grid"
                    gridTemplateColumns="50px 30px 60px 30px 1fr"
                    alignItems="center"
                    gap={0}
                    width="100%"
                  >
                    <Text 
                      noOfLines={1}
                      pl={2}
                    >
                      {dataset.name.replace(/\.[^/.]+$/, '')}
                    </Text>
                    <Text 
                      textAlign="center" 
                      color="gray.300"
                    >
                      |
                    </Text>
                    <Box 
                      bg="blue.100" 
                      px={2} 
                      py={1} 
                      borderRadius="md"
                      textAlign="center"
                    >
                      <Text 
                        color="blue.700" 
                        fontSize="sm" 
                        textTransform="uppercase"
                      >
                        {dataset.type}
                      </Text>
                    </Box>
                    <Text 
                      textAlign="center" 
                      color="gray.300"
                    >
                      |
                    </Text>
                    <Box 
                      overflowX="auto" 
                      whiteSpace="nowrap"
                      css={{
                        '&::-webkit-scrollbar': {
                          height: '6px',
                        },
                        '&::-webkit-scrollbar-track': {
                          background: 'rgba(0, 0, 0, 0.05)',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          background: 'rgba(0, 0, 0, 0.1)',
                          borderRadius: '3px',
                        },
                      }}
                    >
                      {formatVariables(dataset.metadata?.variables).map((variable, idx, arr) => (
                        <Text
                          key={idx}
                          as="span"
                          fontSize="sm"
                          color="gray.600"
                        >
                          {variable}
                          {idx < arr.length - 1 && (
                            <Text 
                              as="span" 
                              color="gray.400" 
                              mx={2}
                            >
                              •
                            </Text>
                          )}
                        </Text>
                      ))}
                    </Box>
                  </Box>
                </Box>
              ))}
              <Box width="100%" maxW="100%">
                <VStack
                  {...getRootProps()}
                  p={4}
                  border="2px dashed"
                  borderColor={isDragActive ? 'blue.400' : 'gray.200'}
                  borderRadius="md"
                  bg={isDragActive ? 'blue.50' : 'transparent'}
                  transition="all 0.2s"
                  spacing={2}
                  width="100%"
                  maxW="100%"
                >
                  <input {...getInputProps()} />
                  <Text textAlign="center">
                    {isDragActive
                      ? "Drop the files here..."
                      : "Drag 'n' drop datasets here, or click to select files"}
                  </Text>
                  <Text textAlign="center" fontSize="sm" color="gray.500" mt={2}>
                    Supported formats: .nc, .csv, .json, .xlsx
                  </Text>
                </VStack>
              </Box>
            </VStack>
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem>
          <h2>
            <AccordionButton>
              <Box flex="1" textAlign="left" fontWeight="bold">
                API Connection
              </Box>
              <AccordionIcon />
            </AccordionButton>
          </h2>
          <AccordionPanel pb={4} maxW="100%" overflowX="hidden">
            <VStack spacing={4} width="100%" maxW="100%">
              <FormControl>
                <FormLabel>API Endpoint</FormLabel>
                <Input placeholder="https://api.example.com/data" />
              </FormControl>
              <FormControl>
                <FormLabel>API Key</FormLabel>
                <Input type="password" placeholder="Enter API key" />
              </FormControl>
              <Button colorScheme="blue">Test Connection</Button>
            </VStack>
          </AccordionPanel>
        </AccordionItem>
      </Accordion>

      {selectedDataset && (
        <DatasetPreview
          dataset={selectedDataset}
          isOpen={!!selectedDataset}
          onClose={() => setSelectedDataset(null)}
        />
      )}
    </Box>
  )
}

